import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, MoreVertical, Settings, LogOut, MessageCircle, LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { notificationsAPI, messagesAPI } from '../services/api';
import NotificationDrawer from './NotificationDrawer';

const MobileHeader = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Notification State
    const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const [unreadMessages, setUnreadMessages] = useState(0);

    useEffect(() => {
        if (user) {
            const fetchUnreadCounts = async () => {
                try {
                    const [notifRes, msgRes] = await Promise.all([
                        notificationsAPI.getUnreadCount(),
                        messagesAPI.getUnreadCount()
                    ]);
                    setUnreadNotifications(notifRes.data.unread_count);
                    setUnreadMessages(msgRes.data.unread_count);
                } catch (error) {
                    console.error('Failed to fetch unread counts:', error);
                }
            };

            fetchUnreadCounts();
            // Poll every 2 minutes
            const interval = setInterval(fetchUnreadCounts, 120000);
            return () => clearInterval(interval);
        }
    }, [user]);

    return (
        <>
            {/* Mobile Header - Visible only on mobile */}
            <div className="lg:hidden sticky top-0 left-0 right-0 bg-[#000] border-b border-gray-800 z-50 px-4 pt-[env(safe-area-inset-top)] w-full">
                <div className="flex justify-between items-center h-16 max-w-md mx-auto w-full">
                    <Link to="/feed" className="flex items-center">
                        <img
                            src="/snapp-logo-new.png"
                            alt="Snapp Logo"
                            className="h-9 w-auto object-contain drop-shadow-md"
                        />
                    </Link>

                    {user ? (
                        /* Logged in - show messages, notifications, menu */
                        <div className="flex items-center gap-1">
                            <Link to="/messages" className="p-2 text-gray-400 hover:text-white transition-colors relative" title="Messages">
                                <MessageCircle className="w-6 h-6" />
                                {unreadMessages > 0 && (
                                    <span className="absolute top-1 right-1 w-4 h-4 bg-snapp-green text-[#0a0a0b] text-[10px] items-center justify-center font-bold rounded-full flex border-2 border-[#0a0a0b]">
                                        {unreadMessages > 9 ? '9+' : unreadMessages}
                                    </span>
                                )}
                            </Link>

                            <button
                                onClick={() => setIsNotificationDrawerOpen(true)}
                                className="p-2 text-gray-400 hover:text-white transition-colors relative"
                                title="Notifications"
                            >
                                <Bell className="w-6 h-6" />
                                {unreadNotifications > 0 && (
                                    <span className="absolute top-1 right-1 w-4 h-4 bg-snapp-green text-[#0a0a0b] text-[10px] items-center justify-center font-bold rounded-full flex border-2 border-[#0a0a0b]">
                                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                                    </span>
                                )}
                            </button>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="p-2 text-gray-400 hover:text-white transition-colors" title="Menu">
                                        <MoreVertical className="w-7 h-7" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48 bg-[#1a1a1b] border-gray-800 text-white rounded-xl shadow-2xl">
                                    <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer hover:bg-white/5 focus:bg-white/5 h-12 rounded-lg m-1">
                                        <Settings className="mr-3 h-5 w-5" />
                                        <span className="font-medium">Ayarlar</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-500 hover:text-red-400 hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-400 h-12 rounded-lg m-1">
                                        <LogOut className="mr-3 h-5 w-5" />
                                        <span className="font-medium">Çıkış Yap</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : (
                        /* Guest - show Login / Sign Up */
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => navigate('/login')}
                                className="flex items-center gap-1.5 px-4 py-2 bg-snapp-green text-[#0a0a0b] font-semibold rounded-lg hover:bg-snapp-green/90 transition-all text-sm"
                            >
                                <LogIn className="w-4 h-4" />
                                Giriş Yap
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {user && (
                <NotificationDrawer
                    isOpen={isNotificationDrawerOpen}
                    onClose={() => setIsNotificationDrawerOpen(false)}
                    onUnreadUpdate={setUnreadNotifications}
                />
            )}
        </>
    );
};

export default MobileHeader;
